import React from 'react';
import { Modal, Descriptions, Tag, Typography, Space, Button, message } from 'antd';
import { ShopOutlined, CopyOutlined } from '@ant-design/icons';
import type { Shipment, ShipmentAddressSummary } from '../../../services/logistics';
import { formatMoney, kgToLbOz, cmToInch } from '../shipping-rates/units';

const { Text } = Typography;

const RECORD_STATUS_COLORS: Record<string, string> = {
  active: 'green',
  voided: 'red',
};

const BATTERY_CHEMISTRY_LABELS: Record<string, string> = {
  lithium_ion: '锂离子',
  lithium_metal: '锂金属',
};
const BATTERY_INSTALLATION_LABELS: Record<string, string> = {
  installed: '安装在设备内',
  packed: '随设备未安装',
  standalone: '单独电池',
};

// 完整地址展示——和购买确认弹窗（PurchaseModal.renderAddressSummary）用的是同一套结构，
// 保持视觉/信息一致，用户在购买时和购买后看到的地址呈现方式不会变样。
const renderFullAddress = (addr: ShipmentAddressSummary | null) => (
  <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
    <Descriptions.Item label="姓名/公司">
      {addr?.name || '-'}{addr?.company ? `（${addr.company}）` : ''}
    </Descriptions.Item>
    <Descriptions.Item label="电话">{addr?.phone || '-'}</Descriptions.Item>
    <Descriptions.Item label="地址" span={2}>
      {addr?.address_line || '-'}{addr?.address_line2 ? `，${addr.address_line2}` : ''}
    </Descriptions.Item>
    <Descriptions.Item label="城市 州 邮编" span={2}>
      {[addr?.city, addr?.state, addr?.postal_code].filter(Boolean).join(' ') || '-'}
    </Descriptions.Item>
    <Descriptions.Item label="国家 (地区)" span={2}>{addr?.country || '-'}</Descriptions.Item>
  </Descriptions>
);

interface ShipmentDetailModalProps {
  shipment: Shipment | null;
  onClose: () => void;
}

const ShipmentDetailModal: React.FC<ShipmentDetailModalProps> = ({ shipment, onClose }) => {
  const weightKg = shipment?.weight_kg != null ? Number(shipment.weight_kg) : null;
  const lengthCm = shipment?.length_cm != null ? Number(shipment.length_cm) : null;
  const widthCm = shipment?.width_cm != null ? Number(shipment.width_cm) : null;
  const heightCm = shipment?.height_cm != null ? Number(shipment.height_cm) : null;
  const weightLbOz = weightKg != null ? kgToLbOz(weightKg) : {};

  const quoted = shipment ? Number(shipment.quoted_shipping_fee) : 0;
  const deposit = shipment ? Number(shipment.deposit_frozen) : 0;
  const final = shipment ? Number(shipment.final_shipping_fee) : 0;
  const purchaseTotal = quoted + deposit;
  const feeAdjustment = final - quoted;
  const adjusted = shipment != null && feeAdjustment !== 0;

  const handleCopyTrackingNumber = async () => {
    if (!shipment) return;
    try {
      await navigator.clipboard.writeText(shipment.tracking_number);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  return (
    <Modal
      title={shipment ? (
        <Space>
          <span>运单详情</span>
          <Text code>{shipment.tracking_number}</Text>
          <Button type="text" size="small" icon={<CopyOutlined />} onClick={handleCopyTrackingNumber} />
          <Tag color={RECORD_STATUS_COLORS[shipment.record_status] || 'default'}>
            {shipment.record_status_display}
          </Tag>
        </Space>
      ) : '运单详情'}
      open={!!shipment}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {shipment && (
        <>
          <h4>包裹信息</h4>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="重量">
              {weightKg != null
                ? `${weightKg.toFixed(2)} kg（约 ${weightLbOz.lb || 0} 磅 ${weightLbOz.oz || 0} 盎司）`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="尺寸（长 x 宽 x 高）">
              {lengthCm != null && widthCm != null && heightCm != null
                ? `${lengthCm} x ${widthCm} x ${heightCm} cm（约 ${cmToInch(lengthCm)} x ${cmToInch(widthCm)} x ${cmToInch(heightCm)} in）`
                : '-'}
            </Descriptions.Item>
          </Descriptions>

          <h4>发件人</h4>
          {renderFullAddress(shipment.sender)}

          <h4>收件人</h4>
          {renderFullAddress(shipment.recipient)}

          <h4>其他信息</h4>
          <Descriptions bordered size="small" column={3} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="签名服务">{shipment.signature_required ? '需要' : '不需要'}</Descriptions.Item>
            <Descriptions.Item label="保险">
              {shipment.insurance_amount
                ? `需要（保额 ${formatMoney(shipment.insurance_amount, shipment.currency)}）`
                : '不需要'}
            </Descriptions.Item>
            <Descriptions.Item label="电池">
              {shipment.battery_declared
                ? `含电池（${BATTERY_CHEMISTRY_LABELS[shipment.battery_chemistry] || shipment.battery_chemistry || '-'}`
                  + `-${BATTERY_INSTALLATION_LABELS[shipment.battery_installation] || shipment.battery_installation || '-'}）`
                : '不含电池'}
            </Descriptions.Item>
          </Descriptions>

          <h4>发货备注</h4>
          <Text style={{ display: 'block', marginBottom: 16, whiteSpace: 'pre-wrap' }}>
            {shipment.remarks || '-'}
          </Text>

          <h4>物流产品与价格</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <Space size={12}>
              <div style={{ width: 48, height: 48, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {shipment.carrier_logo ? (
                  <img src={shipment.carrier_logo} alt={shipment.carrier_name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <ShopOutlined style={{ color: '#bfbfbf', fontSize: 24 }} />
                )}
              </div>
              <div>
                <div><Text strong style={{ fontSize: 16 }}>{shipment.carrier_name}</Text></div>
                <div><Text type="secondary">{shipment.service_name || '-'}</Text></div>
              </div>
            </Space>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ background: '#f0f5ff', borderRadius: 8, padding: '10px 20px' }}>
                <div>购买时运费 {formatMoney(shipment.quoted_shipping_fee, shipment.currency)}</div>
                <div>保证金(可退) {formatMoney(shipment.deposit_frozen, shipment.currency)}</div>
                <div style={{ marginTop: 4 }}>
                  <Text strong>购买时支付总额 {formatMoney(purchaseTotal, shipment.currency)}</Text>
                </div>
              </div>
              <div style={{ background: '#f0f5ff', borderRadius: 8, padding: '10px 20px' }}>
                <div>
                  运费调整{' '}
                  {adjusted ? (
                    <Text style={{ color: feeAdjustment > 0 ? '#f5222d' : '#52c41a' }}>
                      {feeAdjustment > 0 ? '+' : '-'}{Math.abs(feeAdjustment).toFixed(2)}
                    </Text>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
                <div style={{ marginTop: 4 }}>
                  <Text
                    strong
                    style={adjusted ? { color: feeAdjustment > 0 ? '#f5222d' : '#52c41a' } : undefined}
                  >
                    最终运费 {formatMoney(shipment.final_shipping_fee, shipment.currency)}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default ShipmentDetailModal;
